import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowRight, ArrowLeft, Banknote, Scale, Home, GitBranch, HelpCircle } from 'lucide-react';
import { DocType, Motivo, Query, VerifyStatus } from '../types';

const DOC_LABELS: Record<DocType, string> = { CC: 'Cédula de ciudadanía', CE: 'Cédula de extranjería', PA: 'Pasaporte', TI: 'Tarjeta de identidad' };
const MOTIVO_LABELS: Record<Exclude<Motivo, 'otro'>, string> = {
  deudas: 'Cobro de deudas y/o obligaciones', divorcio: 'Proceso de divorcio',
  arrendamiento: 'Contrato de arrendamiento', sucesion: 'Proceso de sucesión',
};
const MOTIVOS: { id: Motivo; label: string; icon: React.ReactNode }[] = [
  { id: 'deudas', label: 'Cobro de deudas', icon: <Banknote size={14} /> },
  { id: 'divorcio', label: 'Proceso de divorcio', icon: <Scale size={14} /> },
  { id: 'arrendamiento', label: 'Arrendamiento', icon: <Home size={14} /> },
  { id: 'sucesion', label: 'Sucesión', icon: <GitBranch size={14} /> },
  { id: 'otro', label: 'Otro motivo', icon: <HelpCircle size={14} /> },
];
const PRICE = 99000;

interface Props {
  isOpen: boolean;
  existingDocNumbers: string[];
  onAddAnother: (query: Query, verifyFn: () => void) => void;
  onDone: () => void;
}

export default function AddAnotherPopup({ isOpen, existingDocNumbers, onAddAnother, onDone }: Props) {
  const [view, setView] = useState<'question' | 'form'>('question');

  // Mini-form state
  const [docType, setDocType] = useState<DocType | null>(null);
  const [docNumber, setDocNumber] = useState('');
  const [motivo, setMotivo] = useState<Motivo | null>(null);
  const [otroTexto, setOtroTexto] = useState('');
  const [error, setError] = useState('');

  const docNumTrimmed = docNumber.trim();
  const isValid = !!docType && docNumTrimmed.length >= 5 && docNumTrimmed.length <= 15 && /^\d+$/.test(docNumTrimmed) && !!motivo && (motivo !== 'otro' || otroTexto.trim().length > 0);

  const resetMiniForm = () => {
    setDocType(null); setDocNumber(''); setMotivo(null); setOtroTexto(''); setError('');
  };

  const handleAddAnother = () => {
    if (!isValid || !docType || !motivo) return;
    if (existingDocNumbers.includes(docNumTrimmed)) {
      setError('Este documento ya fue agregado a la consulta.');
      return;
    }
    const queryId = Date.now().toString();
    const query: Query = {
      id: queryId, docType, docTypeLabel: DOC_LABELS[docType], docNumber: docNumTrimmed,
      motivo, motivoLabel: motivo === 'otro' ? (otroTexto || 'Otro motivo') : MOTIVO_LABELS[motivo as Exclude<Motivo, 'otro'>],
      otroTexto: motivo === 'otro' ? otroTexto : undefined,
      nombreCompleto: '', verifyStatus: 'pending' as VerifyStatus, amount: PRICE,
    };

    resetMiniForm();
    setView('question');
    onAddAnother(query, () => {});
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ backgroundColor: 'rgba(10,22,40,0.65)', backdropFilter: 'blur(6px)' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 24 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="rounded-2xl w-full max-w-md shadow-2xl p-8"
              style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}
            >
              <AnimatePresence mode="wait">
                {view === 'question' ? (
                  <motion.div key="q" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 mx-auto" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                      <Plus size={24} className="text-[#2563EB]" />
                    </div>
                    <h2 className="text-xl font-bold text-center mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                      ¿Deseas añadir otra consulta?
                    </h2>
                    <p className="text-sm text-center mb-7" style={{ color: 'var(--text-secondary)' }}>
                      Puedes consultar a varias personas en un solo pago.
                    </p>
                    <div className="flex flex-col gap-3">
                      <motion.button
                        data-testid="popup-add-another-btn"
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={() => setView('form')}
                        className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl font-bold text-white"
                        style={{ backgroundColor: '#2563EB', boxShadow: '0 4px 20px rgba(37,99,235,0.3)' }}
                      >
                        <Plus size={17} /> Sí, añadir otra consulta
                      </motion.button>
                      <motion.button
                        data-testid="popup-continue-btn"
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={onDone}
                        className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl font-semibold border-2 transition-all"
                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', backgroundColor: 'transparent' }}
                      >
                        No, continuar al pago <ArrowRight size={16} />
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <button onClick={() => { setView('question'); resetMiniForm(); }} className="flex items-center gap-1.5 text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
                      <ArrowLeft size={14} /> Volver
                    </button>
                    <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                      Añadir otra consulta
                    </h2>
                    <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>Ingresa los datos de la persona a consultar.</p>

                    {/* Doc type */}
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>Tipo de documento</p>
                    <div className="flex gap-2 flex-wrap mb-4">
                      {(['CC','CE','PA','TI'] as DocType[]).map(d => (
                        <button key={d} onClick={() => setDocType(d)}
                          className="px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all"
                          style={{ borderColor: docType === d ? '#2563EB' : 'var(--border-color)', backgroundColor: docType === d ? 'rgba(37,99,235,0.08)' : 'transparent', color: docType === d ? '#2563EB' : 'var(--text-secondary)' }}>
                          {d}
                        </button>
                      ))}
                    </div>

                    {/* Doc number */}
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>Número de documento</p>
                    <input
                      value={docNumber} onChange={e => { setDocNumber(e.target.value.replace(/\D/g,'')); setError(''); }}
                      placeholder="Ej: 10234567890"
                      className="w-full px-4 py-3 rounded-xl border-2 text-sm mb-4 outline-none transition-all"
                      style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                    />

                    {/* Motivo */}
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>Motivo de consulta</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {MOTIVOS.map(m => (
                        <button key={m.id} onClick={() => setMotivo(m.id)}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all"
                          style={{ borderColor: motivo === m.id ? '#2563EB' : 'var(--border-color)', backgroundColor: motivo === m.id ? 'rgba(37,99,235,0.08)' : 'transparent', color: motivo === m.id ? '#2563EB' : 'var(--text-secondary)' }}>
                          {m.icon} {m.label}
                        </button>
                      ))}
                    </div>
                    {motivo === 'otro' && (
                      <input value={otroTexto} onChange={e => setOtroTexto(e.target.value)}
                        placeholder="Describe el motivo..."
                        className="w-full px-4 py-3 rounded-xl border-2 text-sm mb-4 outline-none"
                        style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                      />
                    )}

                    {error && <p className="text-sm mb-3 font-medium" style={{ color: '#B45309' }}>{error}</p>}

                    <motion.button
                      data-testid="popup-mini-submit-btn"
                      whileHover={isValid ? { scale: 1.02 } : {}} whileTap={isValid ? { scale: 0.98 } : {}}
                      onClick={handleAddAnother} disabled={!isValid}
                      className="w-full py-3.5 rounded-2xl font-bold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                      style={{ backgroundColor: '#2563EB' }}
                    >
                      <Plus size={17} /> Agregar consulta
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
